import { useState } from 'react';
import { useMutation } from 'react-query';
import { X, FileJson, AlertCircle, CheckCircle } from 'lucide-react';
import { createFlow, updateFlow } from '@/services/flowsService';
import toast from 'react-hot-toast';

interface Props {
  flow?: any;
  onClose: () => void;
  onSuccess: () => void;
}

export function FlowJsonEditor({ flow, onClose, onSuccess }: Props) {
  const [jsonText, setJsonText] = useState(() => {
    if (flow) {
      return JSON.stringify(flow, null, 2);
    }
    return JSON.stringify({
      name: 'Novo Fluxo',
      status: 'draft',
      trigger_type: 'inbound',
      trigger_config: {
        channels: ['whatsapp']
      },
      steps: [
        {
          id: 'step_1',
          type: 'ai_interpret',
          params: {
            min_confidence: 0.6
          }
        },
        {
          id: 'step_2',
          type: 'send_message',
          params: {
            mode: 'text',
            text: 'Olá! Como posso ajudar?'
          }
        },
        {
          id: 'step_3',
          type: 'end'
        }
      ]
    }, null, 2);
  });

  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (flow?.id) {
        return updateFlow(flow.id, data);
      }
      return createFlow(data);
    },
    onSuccess: () => {
      toast.success(flow?.id ? 'Fluxo atualizado!' : 'Fluxo criado com sucesso!');
      onSuccess();
    },
    onError: (err: any) => {
      const errorMsg = err?.response?.data?.error || err?.message || 'Erro ao salvar fluxo';
      setError(errorMsg);
      toast.error(errorMsg);
    },
  });

  const handleSave = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setError(null);
      mutation.mutate(parsed);
    } catch (err: any) {
      const errorMsg = `JSON inválido: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (err: any) {
      setError(`JSON inválido: ${err.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b">
          <div className="flex items-center gap-2">
            <FileJson size={24} className="text-blue-600" />
            <h2 className="text-xl font-bold">{flow ? 'Editar' : 'Novo'} Fluxo (JSON)</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-hidden flex flex-col">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={20} className="text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium">Erro</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-hidden">
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              className="w-full h-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Cole ou edite o JSON do fluxo aqui..."
              spellCheck={false}
            />
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2 mb-2">
              <CheckCircle size={16} className="text-blue-600 mt-0.5" />
              <p className="text-sm font-semibold text-blue-900">Estrutura do JSON</p>
            </div>
            <ul className="text-xs text-blue-800 space-y-1.5 ml-6">
              <li>• <code className="bg-blue-100 px-1 rounded">name</code>: Nome do fluxo (obrigatório)</li>
              <li>• <code className="bg-blue-100 px-1 rounded">status</code>: "draft", "active", "paused" ou "archived"</li>
              <li>• <code className="bg-blue-100 px-1 rounded">trigger_type</code>: "inbound" ou "schedule" (obrigatório)</li>
              <li>• <code className="bg-blue-100 px-1 rounded">trigger_config</code>: {`{ channels: ["whatsapp"] }`} ou {`{ cron: "0 8 * * *" }`}</li>
              <li>• <code className="bg-blue-100 px-1 rounded">steps</code>: Array com {`{ id, type, params }`}</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs font-medium text-blue-900 mb-1">Steps disponíveis:</p>
              <div className="flex flex-wrap gap-1">
                <code className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">ai_interpret</code>
                <code className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">tool_api</code>
                <code className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">format_data</code>
                <code className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">send_message</code>
                <code className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">condition</code>
                <code className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">delay</code>
                <code className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">end</code>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={handleFormat}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Formatar JSON
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={mutation.isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isLoading ? 'Salvando...' : 'Salvar Fluxo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
